import React from 'react';

import type {
  LinearProgressProps,
  ProgressSize,
  ProgressVariant,
} from './Progress.types';

/**
 * Size configuration for LinearProgress
 */
const sizeConfig: Record<ProgressSize, { height: string; labelSize: string }> =
  {
    sm: { height: 'h-1', labelSize: 'text-label-xs' },
    md: { height: 'h-2', labelSize: 'text-label-sm' },
    lg: { height: 'h-3', labelSize: 'text-body-md' },
  };

/**
 * Variant colors for LinearProgress
 */
const variantConfig: Record<ProgressVariant, { fill: string }> = {
  primary: { fill: 'bg-primary-500' },
  contrast: { fill: 'bg-contrast-500' },
  secondary: { fill: 'bg-secondary-500' },
  error: { fill: 'bg-error-500' },
  warning: { fill: 'bg-warning-500' },
  success: { fill: 'bg-success-500' },
  neutral: { fill: 'bg-neutral-400' },
};

/**
 * LinearProgress component for showing progress as a horizontal bar.
 * Supports determinate (percentage) and indeterminate modes.
 */
const LinearProgress = React.forwardRef<HTMLDivElement, LinearProgressProps>(
  (
    {
      value,
      max = 100,
      variant = 'primary',
      size = 'md',
      showLabel = false,
      label,
      'aria-label': ariaLabel,
      trackClassName = '',
      fillClassName = '',
      className = '',
      ...props
    },
    ref
  ) => {
    // Determine if indeterminate mode (no value provided)
    const isIndeterminate = value === undefined;

    // Calculate percentage for determinate mode
    const percentage = isIndeterminate
      ? 0
      : Math.min(100, Math.max(0, (value / max) * 100));

    const config = sizeConfig[size];
    const variantStyles = variantConfig[variant];

    // Track classes
    const trackClasses = [
      'relative w-full overflow-hidden rounded-full',
      // a caller on a contrast-700 surface names its own track, since the
      // default would vanish into it
      trackClassName || 'bg-contrast-700',
      config.height,
      className,
    ].join(' ');

    // Fill classes
    const fillClasses = [
      'h-full rounded-full transition-all duration-slow ease-out',
      // A named fill wins over the variant's: a caller reaches for it to say
      // "this reading is not live", which no variant can express.
      fillClassName || variantStyles.fill,
      isIndeterminate ? 'animate-progress-indeterminate' : '',
    ].join(' ');

    // Fill style for determinate mode
    const fillStyle: React.CSSProperties = isIndeterminate
      ? {}
      : { width: `${percentage}%` };

    // Label to display
    const displayLabel = label ?? `${Math.round(percentage)}%`;

    return (
      <div className="w-full">
        <div
          ref={ref}
          role="progressbar"
          aria-label={ariaLabel}
          aria-valuenow={isIndeterminate ? undefined : percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          className={trackClasses}
          {...props}
        >
          <div className={fillClasses} style={fillStyle} />
        </div>
        {showLabel && !isIndeterminate && (
          <div className={`mt-1 text-neutral-400 ${config.labelSize}`}>
            {displayLabel}
          </div>
        )}
        {/* Inline keyframes for indeterminate animation */}
        {isIndeterminate && (
          <style>{`
            @keyframes progress-indeterminate {
              0% {
                transform: translateX(-100%);
                width: 50%;
              }
              50% {
                width: 30%;
              }
              100% {
                transform: translateX(300%);
                width: 50%;
              }
            }
            .animate-progress-indeterminate {
              animation: progress-indeterminate 1.5s infinite ease-in-out;
            }
          `}</style>
        )}
      </div>
    );
  }
);

LinearProgress.displayName = 'LinearProgress';

export { LinearProgress };
export default LinearProgress;
